import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { fetchUserProgression } from '../apiClient/progression';
import type {
  MapProgression,
  ProgressionMode,
  UserProgression,
} from '@shared/progression';
import { useAuth } from './AuthProvider';

interface ProgressionContextValue {
  progression: UserProgression;
  /** Refetch from the server. Called after each run submit so totals stay live. */
  refresh: () => Promise<void>;
  /**
   * Optimistically merge a single map's new best-ever values into the
   * cache so the homepage trophy strip and TopNav chip update before
   * the network round-trip. The server-side GREATEST() upsert is the
   * source of truth; refresh() reconciles afterwards.
   *
   * The entry MUST carry both `mapSlug` and `mode` because the same slug
   * can exist in two modes (e.g. `on-and-on` pulse + `on-and-on` circles)
   * and we must not collapse them into the same trophy slot.
   */
  applyLocalBest: (entry: MapProgression) => void;
  /**
   * Convenience accessor: best-ever stars+cups for a single (mode, slug)
   * pair (zeroes if none). The mode is required — Pulse and Circles share
   * slugs, so a slug-only lookup would silently surface the wrong row.
   */
  forMap: (mapSlug: string, mode: ProgressionMode) => MapProgression;
}

const EMPTY: UserProgression = {
  perMap: [],
  totals: { stars: 0, cups: 0 },
};

const ProgressionContext = createContext<ProgressionContextValue | null>(null);

export function useProgression(): ProgressionContextValue {
  const ctx = useContext(ProgressionContext);
  if (!ctx) {
    throw new Error('useProgression must be used inside <ProgressionProvider>');
  }
  return ctx;
}

/**
 * Composite identity for cache lookups. Slug alone is no longer unique
 * because the Circles catalogue mirrors Pulse's slugs; pairing with mode
 * keeps each (mode, slug) row in its own slot.
 */
function progressionKey(mode: ProgressionMode, mapSlug: string): string {
  return `${mode}:${mapSlug}`;
}

export function ProgressionProvider({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  const [progression, setProgression] = useState<UserProgression>(EMPTY);

  const refresh = useCallback(async () => {
    try {
      const next = await fetchUserProgression();
      setProgression(next);
    } catch (err) {
      console.warn('Failed to fetch progression:', err);
    }
  }, []);

  useEffect(() => {
    if (status !== 'ready' || !user) {
      setProgression(EMPTY);
      return;
    }
    void refresh();
  }, [status, user, refresh]);

  const applyLocalBest = useCallback((entry: MapProgression) => {
    setProgression((prev) => {
      const targetKey = progressionKey(entry.mode, entry.mapSlug);
      const existingIdx = prev.perMap.findIndex(
        (m) => progressionKey(m.mode, m.mapSlug) === targetKey,
      );
      const previous = existingIdx >= 0 ? prev.perMap[existingIdx] : null;
      const merged: MapProgression = {
        mapSlug: entry.mapSlug,
        mode: entry.mode,
        competitiveEligible: entry.competitiveEligible,
        stars: Math.max(previous?.stars ?? 0, entry.stars),
        cups: Math.max(previous?.cups ?? 0, entry.cups),
      };
      const starsDelta = (merged.competitiveEligible ? merged.stars : 0)
        - (previous?.competitiveEligible ? previous.stars : 0);
      const cupsDelta = (merged.competitiveEligible ? merged.cups : 0)
        - (previous?.competitiveEligible ? previous.cups : 0);
      if (previous && previous.stars === merged.stars && previous.cups === merged.cups
        && previous.competitiveEligible === merged.competitiveEligible) {
        return prev;
      }
      const perMap = [...prev.perMap];
      if (existingIdx >= 0) {
        perMap[existingIdx] = merged;
      } else {
        perMap.push(merged);
      }
      return {
        perMap,
        totals: {
          stars: prev.totals.stars + starsDelta,
          cups: prev.totals.cups + cupsDelta,
        },
      };
    });
  }, []);

  const forMap = useCallback(
    (mapSlug: string, mode: ProgressionMode): MapProgression => {
      const targetKey = progressionKey(mode, mapSlug);
      const found = progression.perMap.find(
        (m) => progressionKey(m.mode, m.mapSlug) === targetKey,
      );
      if (found) {
        return found;
      }
      return { mapSlug, mode, competitiveEligible: false, stars: 0, cups: 0 };
    },
    [progression],
  );

  const value = useMemo<ProgressionContextValue>(
    () => ({ progression, refresh, applyLocalBest, forMap }),
    [progression, refresh, applyLocalBest, forMap],
  );

  return (
    <ProgressionContext.Provider value={value}>{children}</ProgressionContext.Provider>
  );
}
